/**
 * Enable runtime caching.
 * Cache values are saved into the local variable and will be lost if the page is reloaded.
 *
 * Additionally, we can add a custom timeToLive to items after which the item will be invalidated.
 */
export class RuntimeCacheService {
  private static instance: RuntimeCacheService | null = null;

  private readonly cacheMap: Map<string, unknown>;
  private readonly timeouts: Map<string, NodeJS.Timeout>;

  private constructor() {
    this.cacheMap = new Map<string, unknown>();
    this.timeouts = new Map<string, NodeJS.Timeout>();
  }

  public static getInstance() {
    if (RuntimeCacheService.instance === null) {
      RuntimeCacheService.instance = new RuntimeCacheService();
    }

    return RuntimeCacheService.instance;
  }

  /**
   * Store a `value` behind a `key` for the `timeToLive` milliseconds.
   */
  setItem<T>(key: string, value: T, timeToLive: number) {
    this.cacheMap.set(key, value);

    const timeout = this.timeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
    }

    const newTimeout = setTimeout(() => {
      this.clearItem(key);
    }, timeToLive);

    this.timeouts.set(key, newTimeout);
  }

  /**
   * Purge the value by key from the cache.
   */
  clearItem(key: string) {
    const timeout = this.timeouts.get(key);
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }

    this.cacheMap.delete(key);
    this.timeouts.delete(key);
  }

  /**
   * Retrieve the value from the cache. If there is no value stored, we return the default value.
   */
  getItem<T>(key: string, defaultValue: T | null = null) {
    const item = this.cacheMap.get(key) as T | undefined;

    if (item === undefined) {
      return defaultValue;
    }

    return item;
  }

  /**
   * Check if there is an item
   */
  hasItem(key: string): boolean {
    return this.cacheMap.has(key);
  }

  purgeCache() {
    // Remove all cached items
    for (const timeout of this.timeouts.values()) {
      clearTimeout(timeout);
    }

    this.cacheMap.clear();
    this.timeouts.clear();
  }
}
